import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { AlertController, NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Observable, from, interval, of } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';
import { PlatformService } from '../_services/platform.service';
import { environment } from '../environments/environment';
import { NavigationHistoryService } from './_services/navigation-history.service';
import { AppService } from './app.service';
import { WebSocketService } from './web-socket.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  version = environment.version;
  profileName: string = '';
  email: string = '';
  currentLocation: any = null;
  locations: any[] = [];
  publicRoutes = ['/aboutus', '/term_cond', '/privacy'];

  private alreadyPrompted = false;

  readonly isLoggedIn$ = this.appService.isLoggedIn$;

  constructor(
    private navController: NavController,
    private alertController: AlertController,
    private platformService: PlatformService,
    private translateService: TranslateService,
    private appService: AppService,
    private router: Router,
    private swUpdate: SwUpdate,
    private navHistory: NavigationHistoryService,
    private webSocketService: WebSocketService,
  ) {}

  ngOnInit(): void {
    this.initApp();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (!this.publicRoutes.includes(event.url)) {
          this.navHistory.markVisited();
        }
      }
    });
  }

  async initApp(): Promise<void> {
    await this.appService.init().then(() => this.startWebSocket());

    this.refreshAppCountry()
      .pipe(
        concatMap(() => this.refreshSupportedTranslations()),
        tap(() => this.promptUpdates()),
        concatMap(() => {
          if (!this.appService.accessToken) {
            return of(null);
          }

          return this.refreshTokens();
        }),
        concatMap(() => {
          return from(this.platformService.getPlatform()).pipe(
            concatMap((platform) => {
              return of(null);
            }),
          );
        }),

        concatMap(() => {
          if (this.appService.accessToken) {
            // TODO: Technically we need to use the tokens to login
            // return from(this.goToHome()).pipe(
            //   concatMap(() => {
            //     return this.appService.hideSplashScreen();
            //   }),
            // );
            return this.appService.hideSplashScreen();
          }

          return from(this.goToLogin()).pipe(
            concatMap(() => {
              return this.appService.hideSplashScreen();
            }),
          );
        }),
      )
      .subscribe();
  }

  async startWebSocket(): Promise<void> {
    await this.webSocketService.client();
  }

  getProfile(): Observable<any> {
    return this.appService.getProfile().pipe(
      tap((res) => {
        if (!res.success) {
          this.appService.showErrorDialog(res.message, res.errors);
          return;
        }

        if (!res.data.result) {
          return;
        }

        this.profileName = res.data.result.profileName;
        this.email = res.data.result.email;
      }),
      catchError((err, caught) => {
        this.appService.showErrorDialog(err.message);
        return of(null);
      }),
    );
  }

  refreshAppCountry(): Observable<any> {
    return this.appService.refreshAppCountry().pipe(
      tap((res) => {
        if (!res.success) {
          this.appService.showErrorDialog(res.message, res.errors);
          return;
        }
      }),
      catchError((err, caught) => {
        this.appService.showErrorDialog(err.message);
        return of(null);
      }),
    );
  }

  refreshSupportedTranslations(): Observable<any> {
    return this.appService.refreshSupportedTranslations().pipe(
      tap((res) => {
        if (!res.success) {
          this.appService.showErrorDialog(res.message, res.errors);
          return;
        }
        this.translateService.use(this.appService.selectedLanguage);
      }),
      catchError((err, caught) => {
        this.appService.showErrorDialog(err.message);
        return of(null);
      }),
    );
  }

  refreshTokens(): Observable<any> {
    return this.appService.refreshTokens().pipe(
      tap((res) => {
        if (!res.success) {
          this.appService.showErrorDialog(res.message, res.errors);
          return;
        }
      }),
      catchError((err, caught) => {
        this.appService.showErrorDialog(err.message);
        return of(null);
      }),
    );
  }

  async goToHome(): Promise<void> {
    await this.navController.navigateForward(['/landing'], {
      replaceUrl: true,
    });
  }

  async goToLogin(username?: string): Promise<void> {
    await this.navController.navigateForward(['/login'], {
      replaceUrl: true,
      queryParams: {
        username: username,
      },
    });
  }

  async logout(): Promise<void> {
    await this.appService.logout();

    await this.navController.navigateForward(['/login'], {
      replaceUrl: true,
    });
  }

  async showStoresInMap(): Promise<void> {
    await this.navController.navigateForward(['/home'], {
      replaceUrl: true,
    });
  }

  async goToAbout(): Promise<void> {
    const currentUrl = this.router.url;
    this.router.navigate(['/aboutus'], {
      queryParams: { returnUrl: currentUrl },
    });

    // await this.navController.navigateForward(['/aboutus'], {
    //   replaceUrl: true,
    // });
  }

  async goToTerms(): Promise<void> {
    const currentUrl = this.router.url;
    this.router.navigate(['/term_cond'], {
      queryParams: { returnUrl: currentUrl },
    });
    // await this.navController.navigateForward(['/term_cond'], {
    //   replaceUrl: true,
    // });
  }

  async goToPrivacy(): Promise<void> {
    const currentUrl = this.router.url;
    this.router.navigate(['/privacy'], {
      queryParams: { returnUrl: currentUrl },
    });
    // await this.navController.navigateForward(['/privacy'], {
    //   replaceUrl: true,
    // });
  }

  async menuOpened(): Promise<void> {
    this.getProfile().subscribe();
  }

  async promptUpdates(): Promise<void> {
    interval(1 * 60 * 1000).subscribe(() => {
      this.swUpdate.checkForUpdate().catch(console.error);
    });

    const noticeMessage = await this.translateService
      .get('SERVICE_WORKER.TEXT.NOTICE')
      .toPromise();
    const newVersionMessage = await this.translateService
      .get('SERVICE_WORKER.TEXT.NEW_VERSION_AVAILABLE_PROMPT')
      .toPromise();
    const reloadMessage = await this.translateService
      .get('SERVICE_WORKER.TEXT.RELOAD')
      .toPromise();

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe(async (event) => {
        if (event.type === 'VERSION_READY' && !this.alreadyPrompted) {
          this.alreadyPrompted = true;

          const alert = await this.alertController.create({
            backdropDismiss: false,
            buttons: [reloadMessage],
            cssClass: 'app-update-alert',
            header: noticeMessage,
            message: newVersionMessage,
          });

          await alert.present();

          await alert.onWillDismiss();

          await this.swUpdate.activateUpdate();

          window.location.reload();
        }
      });
    }
  }
}
